import { toast } from "react-toastify";

import { TEMPLATE_SIDES } from "./TemplateContext";

import {
  getFileFromS3WithPresigned,
  getFileContentType,
  getFilePath, getFiles, getPresignedURL, uploadFileToS3WithPresigned,
} from "../../../utils/helpers";

import { previewCoa, previewCoaFromSample } from "../../../actions/coaPreview";

export const getTemplateAssetFolder = (side) => (
  side === TEMPLATE_SIDES.ENVIRONMENTAL
    ? "EnvironmentTemplate/Template/"
    : "ProductTemplate/Template/"
);

const getTemplateAssetFolderKey = (side) => (
  side === TEMPLATE_SIDES.ENVIRONMENTAL ? "EnvironmentTemplate" : "ProductTemplate"
);

const isAssetPathForSide = (url, side) => (
  Boolean(url && url.includes(`${getTemplateAssetFolderKey(side)}/`))
);

const fetchTemplateAssetBlob = async (sourceUrl, signal = null) => {
  const fileBlobObj = await getFileFromS3WithPresigned([sourceUrl], "private", signal);
  return fileBlobObj?.[sourceUrl] || null;
};

const mapSectionsForCoaPreview = (sections) => (sections || []).map(({ title, inputs }) => ({
  title: title || "",
  inputs: {
    left: (inputs?.left || []).map(({ fieldName, value, flag }) => ({
      fieldName: fieldName || "",
      value: value ?? "",
      flag: flag ?? "",
    })),
    right: (inputs?.right || []).map(({ fieldName, value, flag }) => ({
      fieldName: fieldName || "",
      value: value ?? "",
      flag: flag ?? "",
    })),
  },
}));

const mapSamplesForCoaPreview = (samples) => (samples || []).map((sample) => ({
  sample_id: sample.sample_id || sample.live_sample_id || "",
  sample_type: sample.sample_type || "",
  received_date: sample.received_date || "",
  test_results: (sample.test_results || []).map((row) => ({
    test: row.test || "",
    test_result: row.test_result || "",
    test_spec: row.test_spec || "",
    test_spec_flag: row.test_spec_flag || "",
    test_method: row.test_method || "",
  })),
}));

const buildTestGridPayload = (samples, uniqueTests) => {
  const columns = (uniqueTests || []).filter(({ selected }) => selected).map(({ test }) => test);
  return {
    columns,
    rows: (samples || []).map((sample) => ({
      sample_id: sample.sample_id || sample.live_sample_id || "",
      results: Object.fromEntries(
        (sample.test_results || [])
          .filter(({ test }) => columns.includes(test))
          .map(({ test, test_result }) => [test, test_result || ""]),
      ),
    })),
  };
};

export const base64ToPdfBlob = (base64) => {
  if (!base64) {
    return null;
  }
  const cleanedBase64 = String(base64).replace(/^data:application\/pdf;base64,/, "");
  if (!cleanedBase64) {
    return null;
  }
  const binary = atob(cleanedBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: "application/pdf" });
};

export const fetchCoaPreviewFromS3 = async (uploadResult) => {
  const {
    uploaded_to_s3_bucket: bucket,
    uploaded_to_s3_key: path,
    uploaded_to_s3_region: region,
  } = uploadResult || {};
  if (!bucket || !path) {
    return null;
  }
  const presigned = await getPresignedURL("get", [{ bucket, path, region }]);
  if (!presigned.success || !presigned.result?.[path]) {
    return null;
  }
  const files = await getFiles(path, presigned.result[path]);
  return files?.[path] || null;
};

/**
 * Build POST /coa/preview/ payload from builder or overlay inputs.
 */
export const buildCoaPreviewPayload = ({
  builderPayload = {},
  side,
  templateId,
  uploadToS3 = false,
  uniqueTests = [],
  enableTests = false,
  isCustomizingCoa = false,
  isTemplateSave = false,
  logoImageUrl,
  signatureImageUrl,
  coaName,
}) => {
  const payload = {
    side,
    upload_to_s3: uploadToS3,
    alignment: builderPayload.alignment || "Left",
    enable_tests: false,
  };

  if (templateId) {
    payload.template_id = templateId;
  }

  if (coaName) {
    payload.coa_name = coaName;
  }

  if (logoImageUrl !== undefined) {
    payload.company_logo_url = logoImageUrl;
  }
  if (signatureImageUrl !== undefined) {
    payload.signature_image_url = signatureImageUrl;
  }

  const optionalFields = {
    template_company_name: builderPayload.company_name,
    company_address: builderPayload.company_address,
    phone_number: builderPayload.phone_number,
    fax_number: builderPayload.fax_number,
    signed_by: builderPayload.signed_by,
    designation: builderPayload.designation,
    statement_inputs: builderPayload.statement_inputs,
  };

  Object.entries(optionalFields).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      payload[key] = value;
    }
  });

  if (builderPayload.sections?.length) {
    payload.sections = mapSectionsForCoaPreview(builderPayload.sections);
  }

  if (!isTemplateSave && isCustomizingCoa && builderPayload.samples?.length) {
    payload.samples = mapSamplesForCoaPreview(builderPayload.samples);
    if (enableTests) {
      payload.enable_tests = true;
      payload.test_grid = buildTestGridPayload(builderPayload.samples, uniqueTests);
    }
  }

  return payload;
};

export const uploadTemplateAsset = async ({ file, type, side }) => {
  if (!file) {
    return "";
  }
  const splitByDot = file.name.trim().split(".");
  const fileEnding = splitByDot.pop();
  const fileName = splitByDot.join(".");
  const fileInfo = {
    folderPath: getTemplateAssetFolder(side),
    fileName: `${fileName}_${Date.now()}.${fileEnding}`,
    file,
    type: file.type,
  };
  const key = getFilePath(fileInfo);
  const fileBlob = await uploadFileToS3WithPresigned([fileInfo], "private");
  if (fileBlob?.[key]?.path) {
    return fileBlob[key].path;
  }
  return "";
};

/**
 * Ensure logo/signature exists under the current side's template folder on S3.
 * Copies from ProductTemplate when importing/reusing cross-side assets.
 */
export const resolveTemplateAssetUrl = async ({
  file,
  sourceUrl,
  type,
  side,
  signal = null,
}) => {
  if (file) {
    return uploadTemplateAsset({ file, type, side });
  }
  if (!sourceUrl) {
    return "";
  }
  if (isAssetPathForSide(sourceUrl, side)) {
    const existingBlob = await fetchTemplateAssetBlob(sourceUrl, signal);
    if (signal?.aborted) {
      return "";
    }
    if (existingBlob) {
      return sourceUrl;
    }
    const oppositeSide = side === TEMPLATE_SIDES.ENVIRONMENTAL
      ? TEMPLATE_SIDES.PRODUCT
      : TEMPLATE_SIDES.ENVIRONMENTAL;
    const alternateUrl = sourceUrl.replace(
      `${getTemplateAssetFolderKey(side)}/`,
      `${getTemplateAssetFolderKey(oppositeSide)}/`,
    );
    if (alternateUrl !== sourceUrl) {
      const alternateBlob = await fetchTemplateAssetBlob(alternateUrl, signal);
      if (signal?.aborted) {
        return "";
      }
      if (alternateBlob) {
        const name = alternateUrl.split("/").pop();
        const assetFile = new File([alternateBlob], name, { type: getFileContentType({ name, type: alternateBlob.type }) });
        const uploadedPath = await uploadTemplateAsset({ file: assetFile, type, side });
        return uploadedPath || sourceUrl;
      }
    }
    return sourceUrl;
  }
  const blob = await fetchTemplateAssetBlob(sourceUrl, signal);
  if (signal?.aborted) {
    return "";
  }
  if (!blob) {
    return sourceUrl;
  }
  const name = sourceUrl.split("/").pop();
  const assetFile = new File([blob], name, { type: getFileContentType({ name, type: blob.type }) });
  const uploadedPath = await uploadTemplateAsset({ file: assetFile, type, side });
  return uploadedPath || sourceUrl;
};

/**
 * Resolve logo/signature S3 paths for live preview (upload new files or reuse existing).
 */
export const resolvePreviewAssetUrls = async ({
  logo,
  signature,
  selectedTemplate,
  isEditingTemplate,
  side,
}) => {
  const urls = {};
  const assets = [
    {
      fileObj: logo, type: "logo_image", urlKey: "logo_image_url", apiKey: "company_logo_url",
    },
    {
      fileObj: signature, type: "signature_image", urlKey: "signature_image_url", apiKey: "signature_image_url",
    },
  ];

  await Promise.all(assets.map(async ({
    fileObj, type, urlKey, apiKey,
  }) => {
    const { file, wasEdited } = fileObj || {};
    const sourceUrl = selectedTemplate?.[urlKey];

    if (wasEdited && !file) {
      urls[apiKey] = "";
      return;
    }

    if (file && (!isEditingTemplate || wasEdited)) {
      urls[apiKey] = await uploadTemplateAsset({ file, type, side });
      return;
    }

    const resolvedUrl = await resolveTemplateAssetUrl({
      file: file && !wasEdited ? file : null,
      sourceUrl,
      type,
      side,
    });
    if (resolvedUrl) {
      urls[apiKey] = resolvedUrl;
    }
  }));

  return urls;
};

export const fetchCoaPreviewPdf = async (previewPayload, signal) => {
  const response = await previewCoa(previewPayload, signal);
  if (response.canceled) {
    return { canceled: true };
  }
  if (!response.success) {
    throw new Error(response.message || "Failed to generate preview.");
  }

  if (response.message === "Success with asset load errors") {
    toast.warn("Preview generated with asset load errors.");
  }

  const { result } = response;
  if (previewPayload.upload_to_s3) {
    const blob = await fetchCoaPreviewFromS3(result);
    if (!blob) {
      throw new Error("Failed to fetch preview from cloud.");
    }
    return {
      blob,
      s3Key: result.uploaded_to_s3_key,
      assetLoad: result.asset_load,
    };
  }

  const blob = base64ToPdfBlob(result?.pdf_base64);
  if (!blob) {
    throw new Error("Preview PDF not available.");
  }
  return {
    blob,
    s3Key: null,
    assetLoad: result?.asset_load,
  };
};

export const fetchCoaPreviewFromSample = async (samplePayload, signal) => {
  try {
    const requestPayload = {
      template_id: samplePayload.template_id,
    };

    if (samplePayload.side) {
      requestPayload.side = samplePayload.side;
    }

    if (samplePayload.sample_unique_identifier) {
      requestPayload.sample_unique_identifier = samplePayload.sample_unique_identifier;
    } else {
      requestPayload.sample_id = samplePayload.sample_id;
      requestPayload.sample_type = samplePayload.sample_type;
      requestPayload.received_date = samplePayload.received_date;
    }

    const response = await previewCoaFromSample(requestPayload, signal);
    if (response.canceled || signal?.aborted) {
      return { success: false, canceled: true };
    }
    if (!response.success) {
      return {
        success: false,
        message: response.message || "Failed to generate preview.",
      };
    }

    const { result } = response;
    const blob = base64ToPdfBlob(result.pdf_base64);
    if (!blob) {
      return {
        success: false,
        message: "Template PDF is not available.",
      };
    }

    return {
      success: true,
      blob,
      fileName: result.filename,
    };
  } catch (error) {
    if (signal?.aborted) {
      return { success: false, canceled: true };
    }
    return {
      success: false,
      message: error.message || "Something went wrong.",
    };
  }
};
